import type { WebsiteImportResult } from "./import-types";
import type { StudioPageContent } from "../model/page-content";
import { extractPageContent } from "./page-content-extractor";
import {
  inspectWebsiteImport,
  type ImportInspection,
} from "./import-inspector";
import {
  analyzeWebsiteRelationshipGraph,
  type WebsiteGraphAnalysis,
} from "./website-graph-analyzer";
import {
  buildWebsiteRelationshipGraph,
  type WebsiteRelationshipGraph,
} from "./website-relationship-graph";
import {
  classifyWebsiteSemantics,
  type WebsiteSemanticClassification,
} from "./website-semantic-classifier";
import { importWebsiteZip } from "./zip-project-importer";
import {
  importCapturedUrlSite,
  type CapturedUrlSite,
} from "./url-import-adapter";
import {
  collectRemoteAssetCandidates,
  type RemoteAssetCandidate,
} from "./remote-asset-candidates";

export type WebsitePageContentAnalysis = {
  pageId: string;
  route: string;
  content: StudioPageContent;
};

export type WebsiteImportWorkflowResult = {
  importResult: WebsiteImportResult;
  inspection: ImportInspection;
  graph: WebsiteRelationshipGraph;
  analysis: WebsiteGraphAnalysis;
  semantics: WebsiteSemanticClassification;
  content: WebsitePageContentAnalysis[];
  remoteAssetCandidates: RemoteAssetCandidate[];
};

export function analyzeWebsiteImport(
  importResult: WebsiteImportResult,
  remoteAssetCandidates: RemoteAssetCandidate[] = [],
): WebsiteImportWorkflowResult {
  const inspection = inspectWebsiteImport(importResult);
  const graph = buildWebsiteRelationshipGraph(importResult);
  const analysis = analyzeWebsiteRelationshipGraph(graph);
  const semantics = classifyWebsiteSemantics(graph, analysis);

  const content = graph.pages.flatMap((graphPage) => {
    const importedPage = importResult.pages.find(
      (page) => page.route === graphPage.route,
    );

    if (!importedPage) {
      return [];
    }

    return [
      {
        pageId: graphPage.id,
        route: graphPage.route,
        content: extractPageContent(importedPage),
      },
    ];
  });

  return {
    importResult,
    inspection,
    graph,
    analysis,
    semantics,
    content,
    remoteAssetCandidates,
  };
}

export async function runWebsiteImportWorkflow(
  zipInput: ArrayBuffer | Uint8Array,
): Promise<WebsiteImportWorkflowResult> {
  const importResult = await importWebsiteZip(zipInput);
  return analyzeWebsiteImport(importResult);
}

export function runCapturedUrlImportWorkflow(
  capture: CapturedUrlSite,
): WebsiteImportWorkflowResult {
  const importResult = importCapturedUrlSite(capture);
  const remoteAssetCandidates = collectRemoteAssetCandidates(capture);

  return analyzeWebsiteImport(importResult, remoteAssetCandidates);
}
